import { Button, Pagination, Select, Space, Table, Tooltip, notification } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { mapStarError } from 'pages/star-shared/star-error';
import { MissionReviewRow, ProgressStatus } from 'pages/star-shared/types';
import {
  CAP_REACHED_WARNING,
  formatDateTime,
  notifyBulk,
  personName,
  reviewAttemptText,
  reviewCapReached,
  reviewQuotaText,
  runBulk,
} from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import {
  Ellipsis,
  NoWrap,
  ProgressStatusTag,
  UserCell,
} from 'pages/star-shared/components/status-tags';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import ReviewDrawer, { ReviewAction } from './review-drawer';
import { colors } from 'theme/tokens';

const LIMIT = 20;

const STATUS_OPTIONS: { value: ProgressStatus; label: string }[] = [
  { value: 'PENDING_REVIEW', label: 'Chờ duyệt' },
  { value: 'DONE', label: 'Đã duyệt' },
  { value: 'REJECTED', label: 'Đã từ chối' },
];

const ACTION_META: Record<ReviewAction, { endpoint: string; verb: string; title: string }> = {
  approve: { endpoint: 'approve', verb: 'Đã duyệt', title: 'Duyệt báo cáo' },
  reject: { endpoint: 'reject', verb: 'Đã từ chối', title: 'Từ chối báo cáo' },
};

const ReviewsTab = () => {
  const [status, setStatus] = useState<ProgressStatus>('PENDING_REVIEW');
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detail, setDetail] = useState<MissionReviewRow | null>(null);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading } = usePagedList<MissionReviewRow>(
    API.starsMissionReviews,
    { status, page, limit: LIMIT },
    { refreshKey }
  );
  const pending = status === 'PENDING_REVIEW';

  const refresh = () => {
    setSelectedRowKeys([]);
    setRefreshKey((k) => k + 1);
  };

  const callReview = (id: number, act: ReviewAction, note?: string) =>
    AxiosUtils.patch(
      `${API.starsMissionReviews}/${id}/${ACTION_META[act].endpoint}`,
      note ? { note } : {}
    );

  const openSingle = (review: MissionReviewRow, act: ReviewAction) => {
    const meta = ACTION_META[act];
    // Doc 10 §4.3: người đã chạm trần tháng vẫn duyệt được nhưng Sao trả = 0 (capped) → nói trước.
    const capReached = act === 'approve' && reviewCapReached(review);
    setAction({
      title: meta.title,
      description: (
        <span>
          {personName(review.user)} — {review.mission?.title} ({review.periodKey}) · thưởng{' '}
          <StarAmount value={review.mission?.rewardStars} />
          {capReached && (
            <>
              {' '}
              <span style={{ color: colors.warningInk, fontWeight: 600 }}>
                — {CAP_REACHED_WARNING} ({review.monthPaid}/{review.monthCap} Sao nộp tay).
              </span>
            </>
          )}
        </span>
      ),
      noteRequired: act === 'reject',
      noteLabel: act === 'reject' ? 'Lý do từ chối' : 'Ghi chú (không bắt buộc)',
      confirmText: act === 'reject' ? 'Từ chối' : 'Duyệt',
      danger: act === 'reject',
      onSubmit: async (note) => {
        try {
          await callReview(review.id, act, note);
          notification.success({ message: `${meta.verb} báo cáo của ${personName(review.user)}.` });
          setDetail(null);
          refresh();
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const openBulk = (act: ReviewAction) => {
    const ids = selectedRowKeys.map(Number);
    if (!ids.length) return;
    const meta = ACTION_META[act];
    setAction({
      title: `${meta.title} — ${ids.length} bản ghi đã chọn`,
      description: 'Ghi chú (nếu có) sẽ áp dụng cho tất cả bản ghi đã chọn.',
      noteRequired: act === 'reject',
      noteLabel: act === 'reject' ? 'Lý do từ chối' : 'Ghi chú (không bắt buộc)',
      confirmText: act === 'reject' ? `Từ chối ${ids.length}` : `Duyệt ${ids.length}`,
      danger: act === 'reject',
      onSubmit: async (note) => {
        const result = await runBulk(ids, (id) => callReview(id, act, note));
        notifyBulk(result, meta.verb);
        refresh();
      },
    });
  };

  const columns: ColumnsType<MissionReviewRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      // Dòng phụ (doc 10 §7.1): "cùng ngày nộp 2 báo cáo · tháng này đã nhận 80/150 Sao nộp tay"
      // (đếm theo ngày VN của lần nộp mới nhất, không phải hôm nay — doc 10 §6.2);
      // máy chủ cũ không trả → UserCell tự hiện username/email như trước.
      title: 'Người nộp',
      key: 'user',
      width: 200,
      render: (_, r) => <UserCell user={r.user} extra={reviewQuotaText(r)} />,
    },
    {
      title: 'Nhiệm vụ',
      key: 'mission',
      render: (_, r) => {
        const attempt = reviewAttemptText(r);
        // Chỉ nhắc khi còn chờ duyệt; dòng đã duyệt/từ chối thì câu "duyệt sẽ không cộng" vô nghĩa.
        const capReached = pending && reviewCapReached(r);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{r.mission?.title || '—'}</div>
            <StarAmount value={r.mission?.rewardStars} strong={false} />
            {attempt && <div style={{ color: colors.textSecondary, fontSize: 12 }}>{attempt}</div>}
            {capReached && (
              <div style={{ color: colors.warningInk, fontSize: 12 }}>
                Đã chạm trần tháng — duyệt sẽ không cộng Sao.
              </div>
            )}
          </div>
        );
      },
    },
    {
      // Mã kỳ "W2026-40" không được bẻ tại dấu gạch.
      title: 'Kỳ',
      dataIndex: 'periodKey',
      key: 'periodKey',
      width: 110,
      render: (v) => <NoWrap>{v || '—'}</NoWrap>,
    },
    {
      title: 'Minh chứng',
      key: 'evidence',
      width: 240,
      // Hàng flex co được: link giữ nguyên, ghi chú cắt “…” theo độ rộng ô.
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {r.evidenceUrl && (
            <Tooltip title={r.evidenceUrl}>
              <a
                href={r.evidenceUrl}
                target="_blank"
                rel="noreferrer"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                <LinkOutlined /> Link
              </a>
            </Tooltip>
          )}
          <Ellipsis value={r.evidenceNote} width={200} />
        </div>
      ),
    },
    {
      title: 'Nộp lúc',
      key: 'createdAt',
      width: 160,
      render: (_, r) => <NoWrap>{formatDateTime(r.createdAt)}</NoWrap>,
    },
    ...(!pending
      ? [
          {
            title: 'Kết quả',
            key: 'status',
            width: 200,
            render: (_: any, r: MissionReviewRow) => (
              <div>
                <ProgressStatusTag value={r.status || status} />
                {r.reviewNote && (
                  <div style={{ color: colors.textSecondary, fontSize: 12 }}>{r.reviewNote}</div>
                )}
              </div>
            ),
          } as ColumnsType<MissionReviewRow>[number],
        ]
      : []),
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 130,
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="Chi tiết">
            <Button type="text" icon={<EyeOutlined />} onClick={() => setDetail(r)} />
          </Tooltip>
          {pending && (
            <>
              <Tooltip title="Duyệt">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => openSingle(r, 'approve')}
                />
              </Tooltip>
              <Tooltip title="Từ chối">
                <Button
                  danger
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => openSingle(r, 'reject')}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Select
          style={{ width: 160 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
            setSelectedRowKeys([]);
          }}
        />
        {pending && (
          <>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!selectedRowKeys.length}
              onClick={() => openBulk('approve')}
            >
              Duyệt đã chọn
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              disabled={!selectedRowKeys.length}
              onClick={() => openBulk('reject')}
            >
              Từ chối đã chọn
            </Button>
            {selectedRowKeys.length > 0 && <span>Đã chọn {selectedRowKeys.length}</span>}
          </>
        )}
      </div>
      <div className="custom-table">
        <Table
          rowSelection={pending ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={{ emptyText: pending ? 'Không có báo cáo chờ duyệt.' : 'Chưa có dữ liệu.' }}
          scroll={{ x: 1300 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={page}
            onChange={setPage}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <ReviewDrawer review={detail} onClose={() => setDetail(null)} onAction={openSingle} />
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default ReviewsTab;
